import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  AssessmentSteps,
  clearSelectedAssessment,
  selectAssessment,
  selectDoneAssessmentList,
  selectInProgressAssessmentList,
  setAssessmentLoading,
} from '../../redux/reducers/assessmentReducer';
import { initializeByAssessment as initFindings } from '../../redux/reducers/findingsReducer';
import { selectCustomFrameworks, selectStandardFrameworks } from '../../redux/reducers/frameworkReducer';
import CopyAssessmentModal from './CopyAssessmentModal';
import CreateAssessmentModal from './CreateAssessmentModal';

function AssessmentSelection({ handleStepChange }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const inProgressAssessments = useSelector(selectInProgressAssessmentList);
  const doneAssessments = useSelector(selectDoneAssessmentList);
  const standardFrameworks = useSelector(selectStandardFrameworks);
  const customFrameworks = useSelector(selectCustomFrameworks);
  const [newAssessmentModalOpen, setNewAssessmentModalOpen] = useState(false);
  const [copyAssessmentModalOpen, setCopyAssessmentModalOpen] = useState(false);

  const handleNewAssessment = () => {
    dispatch(clearSelectedAssessment());
    setNewAssessmentModalOpen(true);
  };

  const handleCloseModal = () => {
    setNewAssessmentModalOpen(false);
    setCopyAssessmentModalOpen(false);
  };

  const handleSelection = (assessmentId, assessmentType) => {
    dispatch(setAssessmentLoading());
    dispatch(selectAssessment({ assessmentId, assessmentType }));
    dispatch(initFindings(assessmentId));
    handleStepChange(AssessmentSteps.PLAN);
  };

  const handleCopy = (assessmentId, assessmentType) => {
    dispatch(selectAssessment({ assessmentId, assessmentType }));
    setCopyAssessmentModalOpen(true);
  };

  const handleView = (assessmentId, assessmentType) => {
    dispatch(selectAssessment({ assessmentId, assessmentType }));
    dispatch(initFindings(assessmentId));
    handleStepChange(AssessmentSteps.DONE);
  };

  function findTitle(id) {
    return (
      standardFrameworks.find((fw) => fw.id === id)?.title ?? customFrameworks.find((fw) => fw.id === id)?.title ?? '?'
    );
  }

  return (
    <Box sx={{ maxWidth: '900px' }}>
      {newAssessmentModalOpen && <CreateAssessmentModal handleClose={handleCloseModal} />}
      {copyAssessmentModalOpen && <CopyAssessmentModal handleClose={handleCloseModal} />}
      <Stack variant="assessmentSelectionHeader" direction="row" spacing={4}>
        <Typography variant="header">{t('assessment.assessmentsInProgress')}</Typography>
        <Button id='new-assessment' variant="contained" endIcon={<AddIcon />} onClick={() => handleNewAssessment()}>
          {t('assessment.newAssessment')}
        </Button>
      </Stack>
      <Stack variant="elevated" spacing={3} sx={{ minWidth: 'max-content' }}>
        {inProgressAssessments.map((assessment) => (
          <Paper key={assessment.id} elevation={4} variant="elevation" subtype="assessmentField">
            <Stack direction="row" variant="itemRow">
              <Stack sx={{ pr: '8px' }}>
                <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                  <Typography id={assessment.name} variant="body1">{assessment.name}</Typography>
                  <Chip
                    variant="inLine"
                    size="small"
                    color="controlChip"
                    label={<Typography variant="chip">{t(`assessment.type.${assessment.assessmentType}`)}</Typography>}
                  />
                  <Chip
                    variant="inLine"
                    size="small"
                    color="controlChip"
                    label={
                      <Typography variant="chip">{`${assessment.completion} ${t('assessment.completedText')}`}</Typography>
                    }
                  />
                </Stack>
                <Typography variant="body2">{findTitle(assessment.frameworkId)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="right">
                <Button
                  id={`select-${assessment.name}`}
                  variant="contained"
                  endIcon={<ChevronRightIcon />}
                  onClick={() => handleSelection(assessment.id, assessment.assessmentType)}
                  sx={{ minWidth: 'max-content' }}
                >
                  {t('assessment.selectAssessment')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
      <Divider sx={{ my: '40px' }} />
      <Stack variant="assessmentSelectionHeader" direction="row">
        <Typography variant="header">{t('assessment.assessmentsDone')}</Typography>
      </Stack>
      <Stack variant="elevated" spacing={3} sx={{ minWidth: 'max-content' }}>
        {doneAssessments.map((assessment) => (
          <Paper key={assessment.id} elevation={4} variant="elevation" subtype="assessmentField">
            <Stack direction="row" variant="itemRow">
              <Stack sx={{ pr: '8px' }}>
                <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                  <Typography id={`${assessment.name}-completed`} variant="body1">{assessment.name}</Typography>
                  <Chip
                    variant="inLine"
                    size="small"
                    color="controlChip"
                    label={<Typography variant="chip">{t(`assessment.type.${assessment.assessmentType}`)}</Typography>}
                  />
                </Stack>
                <Typography variant="body2">{findTitle(assessment.frameworkId)}</Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  id={`copy-${assessment.name}`}
                  variant="outlined"
                  endIcon={<FileCopyIcon />}
                  onClick={() => handleCopy(assessment.id, assessment.assessmentType)}
                  sx={{ minWidth: 'max-content' }}
                >
                  {t('assessment.copyAssessment')}
                </Button>
                <Button
                  id={`view-${assessment.name}`}
                  variant="contained"
                  endIcon={<ChevronRightIcon />}
                  onClick={() => handleView(assessment.id, assessment.assessmentType)}
                  sx={{ minWidth: 'max-content' }}
                >
                  {t('assessment.viewAssessment')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
export default AssessmentSelection;
